import { useCallback, useDeferredValue, useEffect, useMemo } from 'react';

import { fcApis } from '@apis/next/fcApis';
import { zipcodeCityApis } from '@apis/next/zipcodeCityApis';
import type { AutoCompleteConfig, AutoCompleteOption } from '@components/NextForms/TextInput';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { ZipcodeCity } from '@models/next/ZipcodeCity';
import { type MobxZodField, getDecodeResultOr } from '@monoid-dev/mobx-zod-form';
import { isValidJapaneseZipcode } from '@utils/sanitize';
import { runInAction } from 'mobx';
import { pipe } from 'remeda';
import type { ZodString } from 'zod';

export interface JPZipCodeAutoFillOptions {
  zipcodeField: MobxZodField<ZodString>;
  address1Field: MobxZodField<ZodString>;
}

export const useJPZipCodeAutoFill = (options: JPZipCodeAutoFillOptions) => {
  const { zipcodeField, address1Field } = options;

  const zipcode = pipe(getDecodeResultOr(zipcodeField.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 250, [v]),
  );

  const { data } = fcApis.useGet(
    '/fc/pref-and-city-by-zipcode',
    {
      queries: {
        zipcode,
      },
    },
    {
      enabled: isValidJapaneseZipcode(zipcode),
    },
  );

  useEffect(() => {
    if (data) {
      address1Field.setOutput(`${data.pref} ${data.city}`);
    }
  }, [data]);
};

export interface CNZipCodeAutoCompleteOptions {
  zipcodeField: MobxZodField<ZodString>;
  address1Field: MobxZodField<ZodString>;
  address1CNField: MobxZodField<ZodString>;
}

export interface CNZipCodeAutoComplete {
  zipcode: AutoCompleteConfig<ZipcodeCity>;
  address1: AutoCompleteConfig<ZipcodeCity>;
  address1_cn: AutoCompleteConfig<ZipcodeCity>;
}

export const useCNZipCodeAutoComplete = (
  options: CNZipCodeAutoCompleteOptions,
): CNZipCodeAutoComplete => {
  const zipcode = pipe(getDecodeResultOr(options.zipcodeField.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const address1 = pipe(getDecodeResultOr(options.address1Field.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const address1_cn = pipe(getDecodeResultOr(options.address1CNField.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const cityByZipcodeQuery = zipcodeCityApis.useGet(
    '/zipcode-city/city/:zipcode',
    {
      params: {
        zipcode,
      },
    },
    {
      enabled: !!zipcode,
      keepPreviousData: true,
    },
  );

  const zipcodeByCityQuery = zipcodeCityApis.useGet(
    '/zipcode-city/zipcode/:city',
    {
      params: {
        city: address1,
      },
      queries: {
        enable_zh: false,
      },
    },
    {
      enabled: !!address1,
      keepPreviousData: true,
    },
  );

  const zipcodeByCityCNQuery = zipcodeCityApis.useGet(
    '/zipcode-city/zipcode/:city',
    {
      params: {
        city: address1_cn,
      },
      queries: {
        enable_zh: true,
      },
    },
    {
      enabled: !!address1_cn,
      keepPreviousData: true,
    },
  );

  const zipcodeOptions = useDeferredValue(
    useMemo(
      () =>
        (cityByZipcodeQuery.data ?? []).slice(0, 100).map(
          (value): AutoCompleteOption<ZipcodeCity> => ({
            label: `${value.zipcode} ${value.province}, ${value.city}`,
            value,
          }),
        ),
      [cityByZipcodeQuery.data],
    ),
  );

  const address1Options = useDeferredValue(
    useMemo(
      () =>
        (zipcodeByCityQuery.data ?? []).slice(0, 100).map(
          (value): AutoCompleteOption<ZipcodeCity> => ({
            label: `${value.province}, ${value.city} ${value.zipcode}`,
            value,
          }),
        ),
      [zipcodeByCityQuery.data],
    ),
  );

  const address1CNOptions = useDeferredValue(
    useMemo(
      () =>
        (zipcodeByCityCNQuery.data ?? []).slice(0, 100).map(
          (value): AutoCompleteOption<ZipcodeCity> => ({
            label: `${value.province_zh}, ${value.city_zh} ${value.province}, ${value.city} ${value.zipcode}`,
            value,
          }),
        ),
      [zipcodeByCityCNQuery.data],
    ),
  );

  const onSelect = useCallback(
    (option: AutoCompleteOption<ZipcodeCity>) => {
      runInAction(() => {
        options.zipcodeField.setOutput(option.value.zipcode);
        options.address1Field.setOutput(option.value.province + ', ' + option.value.city);
        options.address1CNField.setOutput(option.value.province_zh + ', ' + option.value.city_zh);
      });
    },
    [options.zipcodeField, options.address1Field],
  );

  return {
    zipcode: {
      autoCompleteOptions: zipcodeOptions,
      filter: (_, o) => o,
      onSelect,
    },
    address1: {
      autoCompleteOptions: address1Options,
      filter: (_, o) => o,
      onSelect,
    },
    address1_cn: {
      autoCompleteOptions: address1CNOptions,
      filter: (_, o) => o,
      onSelect,
    },
  };
};
